import {postData} from "../../../../../api/componentAPIs"
import appConstants from "../../../../../appConstants";
import MESSAGES from "../../../../../appMessages";
import Events from "../../../../../utilities/EventEmitter";
import pickupOperationsLogger from "../../../../header/dispatchHeader/PickupEventHandler/PickupOperationsLogger";
import { getPickupIdList, checkIfGroupedPickupsInSelection, getPickupDetailsWithAllGroupedPickups } from './pickupActionUtils';
// We need confirmation to assign/unassign/reassign pickups in a group when only one pickup is selected in a group
// This function will check the flag isConfirmationNeeded and if true, it will show the confirmation dialog
// If the flag is false, it will directly execute the action
const handlePickupConfirmation = ({
  eventType,
  executeAction,
  terminalCode,
  pickupDetails,
  glEventHub,
  isConfirmationNeeded,
  pickupOperationTimeTrackingId,
  requestObjKey = 'assignPickupDetails' // Default key for request object
}) => {
  const requestObj = {
    terminalCode,
    [requestObjKey]: pickupDetails
  };

  if (isConfirmationNeeded) {
    // An event is emitted, which will be caught by an event handler in PickupEventHandler component
    return new Promise((resolve, reject) => {
      Events.emit(eventType, {
        onConfirm: () =>
          executeAction(requestObj, glEventHub, pickupDetails, pickupOperationTimeTrackingId)
            .then(resolve)
            .catch(reject),
        onCancel: () => reject()
      });
    });
  }
  return executeAction(requestObj, glEventHub, pickupDetails, pickupOperationTimeTrackingId);
};

// Helper function to execute the assign pickup action
const executeAssignPickup = (requestObj, glEventHub, assignPickupDetails, pickupOperationTimeTrackingId) => {
  glEventHub?.emit('DispatchEvents', 'ShowLoaderDialog', [
    {
      label: 'Pickup assignment',
      eventName: appConstants.PICKUP_EVENTS.ASSIGN_PICKUP,
      pickupIds: getPickupIdList(assignPickupDetails),
      pickupOperationTimeTrackingId
    }
  ]);
  return postData('AssignPickup', requestObj).then(handleResponse(glEventHub, 'Pickup assignment', pickupOperationTimeTrackingId));
};

const executeUnassignPickup = (requestObj, glEventHub, assignPickupDetails, pickupOperationTimeTrackingId) => {
  glEventHub?.emit("DispatchEvents", "ShowLoaderDialog", [
    {
      label: "Pickup unassignment",
      eventName: appConstants.PICKUP_EVENTS.UNASSIGN_PICKUP,
      pickupIds: getPickupIdList(assignPickupDetails),
      pickupOperationTimeTrackingId
    }
  ]);
  return postData("UnassignPickup", requestObj).then(handleResponse(glEventHub, 'Pickup unassignment', pickupOperationTimeTrackingId));
};

const executeReassignPickup = (requestObj, glEventHub, pickupDetails, pickupOperationTimeTrackingId) => {
  glEventHub?.emit("DispatchEvents", "ShowLoaderDialog", [
    {
      label: "Pickup reassignment",
      eventName: appConstants.PICKUP_EVENTS.ASSIGN_PICKUP,
      pickupIds: getPickupIdList(pickupDetails),
      pickupOperationTimeTrackingId
    }
  ]);
  return postData("ReassignPickup", requestObj).then(handleResponse(glEventHub, 'Pickup reassignment', pickupOperationTimeTrackingId));
};

export const assignPickup = ({
  assignPickupDetails,
  terminalCode,
  glEventHub,
  DispatchStore,
  enqueueSnackbar,
  showConfirmation = true
}) => {
  const driverId = assignPickupDetails?.[0]?.driverId;
  const driverError = isDriverValid({driverId, DispatchStore, enqueueSnackbar});
  if(driverError) {
    return driverError;
  }
  const isConfirmationNeeded =
    showConfirmation &&
    checkIfGroupedPickupsInSelection(assignPickupDetails, DispatchStore);

  const pickupDetailsWithAllGroupedPickups = getPickupDetailsWithAllGroupedPickups(
    assignPickupDetails,
    DispatchStore
  );

  const pickupIds = getPickupIdList(pickupDetailsWithAllGroupedPickups);
  const pickupOperationTimeTrackingId = pickupOperationsLogger.initializePickupOperationTracking('assign', {
    pickupIds,
    driverId
  });

  return handlePickupConfirmation({
    eventType: appConstants.PICKUP_EVENTS.SHOW_PICKUP_ASSIGNMENT_CONFIRMATION,
    executeAction: executeAssignPickup,
    terminalCode,
    pickupDetails: pickupDetailsWithAllGroupedPickups,
    glEventHub,
    isConfirmationNeeded,
    pickupOperationTimeTrackingId
  });
};

export const unassignPickup = ({
  pickups,
  driverId,
  terminalCode,
  glEventHub,
  DispatchStore,
  showConfirmation = true
}) => {
  const assignPickupDetails = createAssignPickupDetails(pickups, { driverId: driverId });
  const isConfirmationNeeded =
    showConfirmation && checkIfGroupedPickupsInSelection(assignPickupDetails, DispatchStore);

  const pickupDetailsWithAllGroupedPickups = getPickupDetailsWithAllGroupedPickups(
    assignPickupDetails,
    DispatchStore
  );

  const pickupIds = getPickupIdList(pickupDetailsWithAllGroupedPickups);
  const pickupOperationTimeTrackingId = pickupOperationsLogger.initializePickupOperationTracking('unassign', {
    pickupIds,
    driverId
  });

  return handlePickupConfirmation({
    eventType: appConstants.PICKUP_EVENTS.SHOW_PICKUP_UNASSIGNMENT_CONFIRMATION,
    executeAction: executeUnassignPickup,
    terminalCode,
    pickupDetails: pickupDetailsWithAllGroupedPickups,
    glEventHub,
    isConfirmationNeeded,
    pickupOperationTimeTrackingId
  });
};


export const reassignPickup = ({ pickupDetails, terminalCode, glEventHub, DispatchStore, showConfirmation = true, enqueueSnackbar }) => {
  const driverIdToReassign = pickupDetails?.[0]?.driverIdToAssign;
  const driverError = isDriverValid({driverId: driverIdToReassign, DispatchStore, enqueueSnackbar});
  if(driverError) {
    return driverError;
  }
  const isConfirmationNeeded =
    showConfirmation &&
    checkIfGroupedPickupsInSelection(pickupDetails, DispatchStore);

  const pickupDetailsWithAllGroupedPickups = getPickupDetailsWithAllGroupedPickups(
    pickupDetails,
    DispatchStore
  );

  const pickupIds = getPickupIdList(pickupDetailsWithAllGroupedPickups);
  const driverId = pickupDetails[0]?.driverId;
  const pickupOperationTimeTrackingId = pickupOperationsLogger.initializePickupOperationTracking('reassign', {
    pickupIds,
    driverId
  });

  return handlePickupConfirmation({
    eventType: appConstants.PICKUP_EVENTS.SHOW_PICKUP_REASSIGNMENT_CONFIRMATION,
    executeAction: executeReassignPickup,
    terminalCode,
    pickupDetails: pickupDetailsWithAllGroupedPickups,
    glEventHub,
    isConfirmationNeeded,
    requestObjKey: 'reassignPickupDetail',
    pickupOperationTimeTrackingId
  });
};

// ================================ Helper functions ================================

export const createAssignPickupDetails = (pickups, driverDetails = {}, isReassign = false) => {
  const groupedPickups = pickups.reduce((groups, pickup) => {
    const groupId = pickup.groupId;
    if (!groups[groupId]) {
      groups[groupId] = {
        pickupIds: [],
        driverIdToUnassign: pickup.driverId
      };
    }
    groups[groupId].pickupIds.push(pickup.pickupId);
    return groups;
  }, {});

  return Object.entries(groupedPickups).map(([groupId, groupData]) => ({
    pickupId: groupData.pickupIds,
    groupId,
    ...(isReassign && { driverIdToUnassign: groupData.driverIdToUnassign }),
    ...driverDetails
  }));
};

const handleResponse = (glEventHub, label, pickupOperationTimeTrackingId) => (response) => {
  if (Math.trunc(response?.status/100) !== 2) {
    glEventHub?.emit("DispatchEvents", 'HideLoaderDialog', [{ isAPICallFailed: true }]);
    const errMsg = getFailedAPIRequestErrorMessage(label, response?.message);
    Events.emit('PickupAssignmentErrorNotification', errMsg);
    throw new Error(response?.message)
  }
  
  pickupOperationTimeTrackingId && pickupOperationsLogger.recordPickupModificationApiResponse(pickupOperationTimeTrackingId, response?.config?.url); //record api response time and browser performance
  
  return response;
}

const getFailedAPIRequestErrorMessage = (label, errorMsg) => {
  return errorMsg && typeof errorMsg === 'string'
    ? `${label} error: ${errorMsg}`
    : `${label} request failed`;
};

const isDriverValid = ({driverId, DispatchStore, enqueueSnackbar}) => {
  if(!driverId) {
    return Promise.reject(new Error("Valid driver is required for pickup assignment"));
  }
  const driverRoutes = DispatchStore.getDriverRoutes(driverId);

  if(driverRoutes.length === 0) {
    enqueueSnackbar(MESSAGES.EQUIPMENT.TRAILER_MISSING, {
      variant: 'error'
    });
    return Promise.reject(new Error(MESSAGES.EQUIPMENT.TRAILER_MISSING));
  }
}